// src/design-system/statCardTheme.ts
import type { Tone } from "./tone";

/** ألوان كروت المؤشرات — منسوخة حرفياً من تصميم fleet_analytics_dashboard.html المعتمد
 * (بدون أي تعديل على القيم): 4 درجات فقط (أخضر/سماوي/كهرماني/وردي). كل قيمة `Tone` عندنا
 * تُطابَق على أقرب درجة من الأربعة، لأن الملف المرجعي نفسه لا يحتوي غيرها. */
export type StatCardHue = "green" | "cyan" | "amber" | "pink";

export type StatCardTheme = {
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  stripGradient: string;
  stripGlow: string;
  iconBg: string;
  iconBorder: string;
  iconColor: string;
  iconGlow: string;
};

const HUES: Record<StatCardHue, StatCardTheme> = {
  green: {
    cardBg: "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(15,23,42,0.9) 100%)",
    cardBorder: "rgba(16,185,129,0.55)",
    cardShadow: "0 0 25px rgba(16,185,129,0.18), inset 0 0 15px rgba(16,185,129,0.06)",
    stripGradient: "linear-gradient(to bottom, #34d399, #059669)",
    stripGlow: "0 0 15px #34d399, 0 0 30px #10b981",
    iconBg: "rgba(16,185,129,0.15)",
    iconBorder: "rgba(16,185,129,0.3)",
    iconColor: "#34d399",
    iconGlow: "0 0 10px rgba(16,185,129,0.2)",
  },
  cyan: {
    cardBg: "linear-gradient(135deg, rgba(6,182,212,0.08) 0%, rgba(15,23,42,0.9) 100%)",
    cardBorder: "rgba(6,182,212,0.55)",
    cardShadow: "0 0 25px rgba(6,182,212,0.18), inset 0 0 15px rgba(6,182,212,0.06)",
    stripGradient: "linear-gradient(to bottom, #22d3ee, #0891b2)",
    stripGlow: "0 0 15px #22d3ee, 0 0 30px #06b6d4",
    iconBg: "rgba(6,182,212,0.15)",
    iconBorder: "rgba(6,182,212,0.3)",
    iconColor: "#22d3ee",
    iconGlow: "0 0 10px rgba(6,182,212,0.2)",
  },
  amber: {
    cardBg: "linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(15,23,42,0.9) 100%)",
    cardBorder: "rgba(245,158,11,0.55)",
    cardShadow: "0 0 25px rgba(245,158,11,0.18), inset 0 0 15px rgba(245,158,11,0.06)",
    stripGradient: "linear-gradient(to bottom, #fbbf24, #d97706)",
    stripGlow: "0 0 15px #fbbf24, 0 0 30px #f59e0b",
    iconBg: "rgba(245,158,11,0.15)",
    iconBorder: "rgba(245,158,11,0.3)",
    iconColor: "#fbbf24",
    iconGlow: "0 0 10px rgba(245,158,11,0.2)",
  },
  pink: {
    cardBg: "linear-gradient(135deg, rgba(244,63,94,0.08) 0%, rgba(15,23,42,0.9) 100%)",
    cardBorder: "rgba(244,63,94,0.55)",
    cardShadow: "0 0 25px rgba(244,63,94,0.18), inset 0 0 15px rgba(244,63,94,0.06)",
    stripGradient: "linear-gradient(to bottom, #fb7185, #e11d48)",
    stripGlow: "0 0 15px #fb7185, 0 0 30px #f43f5e",
    iconBg: "rgba(244,63,94,0.15)",
    iconBorder: "rgba(244,63,94,0.3)",
    iconColor: "#fb7185",
    iconGlow: "0 0 10px rgba(244,63,94,0.2)",
  },
};

/** تطابق أقرب Tone عندنا مع إحدى الدرجات الأربعة في التصميم المعتمد. */
export const TONE_HUE: Record<Tone, StatCardHue> = {
  ok: "green",
  info: "cyan",
  warn: "amber",
  bad: "pink",
  violet: "cyan",
  gold: "amber",
  mute: "cyan",
};

export const STAT_CARD_THEME: Record<Tone, StatCardTheme> = Object.fromEntries(
  (Object.keys(TONE_HUE) as Tone[]).map((tone) => [tone, HUES[TONE_HUE[tone]]]),
) as Record<Tone, StatCardTheme>;
